// CRM client type definitions

export type CustomerTier = 'VIP' | 'REGULAR' | 'NEW' | 'AT_RISK' | 'CHURNED';

export interface CustomerCRM {
  id: string;
  name: string;
  phone: string;
  email?: string;
  customerTier: CustomerTier;
  tierLabel: string;
  tierColor: string;
  engagementScore: number;
  bookingCount: number;
  totalSpent: number;
  currency: string;
  tags: string[];
  firstBookingAt: Date | null;
  lastBookingAt: Date | null;
  lastInteraction: Date | null;
  createdAt: Date;
}

export interface CustomerProfile {
  customer: CustomerCRM & {
    notes?: string;
  };
  bookings: BookingListItemClient[];
  bookingStats: BookingStatsClient;
  spendingStats: SpendingStats;
  serviceHistory: ServiceHistoryItem[];
}

export interface BookingListItemClient {
  id: string;
  scheduledAt: Date;
  serviceName: string;
  staffName?: string;
  status: string;
  totalPrice: number;
}

export interface BookingStatsClient {
  total: number;
  completed: number;
  cancelled: number;
  noShow: number;
  upcoming: number;
}

export interface SpendingStats {
  totalSpent: number;
  averagePerVisit: number;
  lastPaymentDate: Date | null;
}

export interface ServiceHistoryItem {
  serviceName: string;
  count: number;
  lastUsed: Date;
}

export interface CustomerPreferences {
  preferredStaff?: string;
  preferredTime?: string;
  notes?: string;
}

export interface CRMDashboardStats {
  vipCount: number;
  newThisMonth: number;
  needsAttention: number;
  avgEngagement: number;
}

export function getEngagementColor(score: number): string {
  if (score >= 75) return 'text-green-600';
  if (score >= 50) return 'text-blue-600';
  if (score >= 25) return 'text-orange-600';
  return 'text-red-600';
}

/**
 * Map tier color identifier to Tailwind CSS classes.
 * The API returns semantic color names (e.g., 'yellow', 'orange', 'blue', 'gray'),
 * and the frontend is responsible for mapping to actual CSS classes.
 */
export function getTierColorClasses(color: string): string {
  const colorMap: Record<string, string> = {
    yellow: 'bg-yellow-100 text-yellow-800',
    orange: 'bg-orange-100 text-orange-800',
    blue: 'bg-blue-100 text-blue-800',
    gray: 'bg-gray-100 text-gray-800',
  };
  return colorMap[color] || 'bg-gray-100 text-gray-800';
}
